import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import {
  Beneficiary,
  BeneficiaryCreate,
  BeneficiaryUpdate,
  BeneficiaryDisplay,
} from '@core/entities/models/beneficiary.model';
import { PaginatedList } from '@core/entities/models/paginated-list.model';
import { ApiEndpoints } from '@core/config/api.config';
import { skipGlobalLoading } from '@core/interceptors/loading-context';

/**
 * Parámetros de filtrado para listar beneficiarios según OpenAPI.
 */
export interface BeneficiaryListParams {
  offset?: number;
  limit?: number;
  /** Filtrar por país */
  countryId?: string;
  /** Filtrar por partner */
  partnerId?: string;
  /** Filtro de búsqueda (max 128 chars) */
  filter?: string;
  /** Campo de ordenación según OpenAPI */
  sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'email' | 'firstName' | 'lastName';
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Servicio para gestionar beneficiarios.
 * Implementa las operaciones CRUD según OpenAPI spec.
 */
@Injectable({ providedIn: 'root' })
export class BeneficiaryService {
  private http: HttpClient;

  private constructor() {
    this.http = inject(HttpClient);
  }

  /**
   * Transforma los datos de un beneficiario para mostrar valores legibles
   */
  transformBeneficiaryData(beneficiary: Beneficiary): BeneficiaryDisplay {
    return {
      ...beneficiary,
      birthCountry: this.transformCountryCode(beneficiary.birthCountry),
      hostCountry: this.transformCountryCode(beneficiary.hostCountry),
      birthDate: this.formatDateToDDMMYYYY(beneficiary.birthDate),
    };
  }

  /**
   * Formatea una fecha a formato dd/mm/aaaa
   */
  private formatDateToDDMMYYYY(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Transforma código de país a nombre completo
   */
  private transformCountryCode(countryCode: string): string {
    if (!countryCode) return countryCode;

    const countryNames: { [key: string]: string } = {
      AD: 'Andorra',
      AE: 'United Arab Emirates',
      AF: 'Afghanistan',
      AL: 'Albania',
      AM: 'Armenia',
      AR: 'Argentina',
      AT: 'Austria',
      AU: 'Australia',
      AZ: 'Azerbaijan',
      BA: 'Bosnia and Herzegovina',
      BD: 'Bangladesh',
      BE: 'Belgium',
      BF: 'Burkina Faso',
      BG: 'Bulgaria',
      BH: 'Bahrain',
      BJ: 'Benin',
      BN: 'Brunei',
      BO: 'Bolivia',
      BR: 'Brazil',
      BT: 'Bhutan',
      BY: 'Belarus',
      CA: 'Canada',
      CF: 'Central African Republic',
      CH: 'Switzerland',
      CI: 'Ivory Coast',
      CL: 'Chile',
      CM: 'Cameroon',
      CN: 'China',
      CO: 'Colombia',
      CU: 'Cuba',
      CV: 'Cape Verde',
      CY: 'Cyprus',
      CZ: 'Czech Republic',
      DE: 'Germany',
      DK: 'Denmark',
      DZ: 'Algeria',
      EC: 'Ecuador',
      EG: 'Egypt',
      ES: 'Spain',
      FI: 'Finland',
      FR: 'France',
      GB: 'United Kingdom',
      GE: 'Georgia',
      GH: 'Ghana',
      GM: 'Gambia',
      GN: 'Guinea',
      GR: 'Greece',
      HK: 'Hong Kong',
      HR: 'Croatia',
      HU: 'Hungary',
      ID: 'Indonesia',
      IE: 'Ireland',
      IL: 'Israel',
      IN: 'India',
      IQ: 'Iraq',
      IR: 'Iran',
      IS: 'Iceland',
      IT: 'Italy',
      JO: 'Jordan',
      JP: 'Japan',
      KG: 'Kyrgyzstan',
      KH: 'Cambodia',
      KP: 'North Korea',
      KR: 'South Korea',
      KW: 'Kuwait',
      KZ: 'Kazakhstan',
      LA: 'Laos',
      LB: 'Lebanon',
      LI: 'Liechtenstein',
      LK: 'Sri Lanka',
      LR: 'Liberia',
      LU: 'Luxembourg',
      LY: 'Libya',
      MA: 'Morocco',
      MC: 'Monaco',
      MD: 'Moldova',
      ME: 'Montenegro',
      MK: 'North Macedonia',
      ML: 'Mali',
      MM: 'Myanmar',
      MN: 'Mongolia',
      MO: 'Macau',
      MR: 'Mauritania',
      MT: 'Malta',
      MU: 'Mauritius',
      MV: 'Maldives',
      MX: 'Mexico',
      MY: 'Malaysia',
      NE: 'Niger',
      NG: 'Nigeria',
      NL: 'Netherlands',
      NO: 'Norway',
      NP: 'Nepal',
      NZ: 'New Zealand',
      OM: 'Oman',
      PE: 'Peru',
      PH: 'Philippines',
      PK: 'Pakistan',
      PL: 'Poland',
      PT: 'Portugal',
      PY: 'Paraguay',
      QA: 'Qatar',
      RO: 'Romania',
      RS: 'Serbia',
      RU: 'Russia',
      SA: 'Saudi Arabia',
      SE: 'Sweden',
      SG: 'Singapore',
      SI: 'Slovenia',
      SK: 'Slovakia',
      SL: 'Sierra Leone',
      SM: 'San Marino',
      SN: 'Senegal',
      SY: 'Syria',
      TD: 'Chad',
      TG: 'Togo',
      TH: 'Thailand',
      TJ: 'Tajikistan',
      TL: 'East Timor',
      TM: 'Turkmenistan',
      TN: 'Tunisia',
      TR: 'Turkey',
      TW: 'Taiwan',
      UA: 'Ukraine',
      US: 'United States',
      UY: 'Uruguay',
      UZ: 'Uzbekistan',
      VA: 'Vatican City',
      VE: 'Venezuela',
      VN: 'Vietnam',
      YE: 'Yemen',
      ZA: 'South Africa',
    };

    return countryNames[countryCode] || countryCode;
  }

  // ---------------------------------------------------------------------------
  // API pública - Operaciones CRUD de beneficiarios
  // ---------------------------------------------------------------------------

  /**
   * Obtiene un listado paginado de beneficiarios.
   * GET /beneficiaries (ListBeneficiaryOperation)
   *
   * @param params - Parámetros de paginación y filtrado (offset, limit, countryId, partnerId)
   * @returns Observable con el listado paginado de beneficiarios
   */
  getAll(params?: BeneficiaryListParams): Observable<PaginatedList<Beneficiary>> {
    let httpParams = new HttpParams();

    if (params?.offset !== undefined) {
      httpParams = httpParams.set('offset', params.offset.toString());
    }
    if (params?.limit !== undefined) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params?.countryId) {
      httpParams = httpParams.set('countryId', params.countryId);
    }
    if (params?.partnerId) {
      httpParams = httpParams.set('partnerId', params.partnerId);
    }
    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http
      .get<PaginatedList<Beneficiary>>(ApiEndpoints.beneficiaries.base, {
        params: httpParams,
        context: skipGlobalLoading(),
      })
      .pipe(map((response) => response));
  }

  /**
   * Obtiene un listado paginado de beneficiarios con datos transformados para visualización.
   * GET /beneficiaries (ListBeneficiaryOperation)
   *
   * @param params - Parámetros de paginación y filtrado (offset, limit, countryId, partnerId)
   * @returns Observable con el listado paginado de beneficiarios transformados
   */
  getAllForDisplay(params?: BeneficiaryListParams): Observable<PaginatedList<BeneficiaryDisplay>> {
    return this.getAll(params).pipe(
      map((response) => ({
        ...response,
        items: response.items.map((beneficiary) => this.transformBeneficiaryData(beneficiary)),
      })),
    );
  }

  /**
   * Obtiene un beneficiario por su ID.
   * GET /beneficiaries/{beneficiaryId} (GetBeneficiaryOperation)
   *
   * @param id - Identificador único del beneficiario
   * @returns Observable con el beneficiario solicitado
   */
  getById(id: string): Observable<Beneficiary> {
    return this.http.get<Beneficiary>(ApiEndpoints.beneficiaries.byId(id));
  }

  /**
   * Crea un nuevo beneficiario.
   * POST /beneficiaries (CreateBeneficiaryOperation)
   *
   * @param beneficiary - Datos del beneficiario a crear
   * @returns Observable con el beneficiario creado
   */
  create(beneficiary: BeneficiaryCreate): Observable<Beneficiary> {
    return this.http.post<Beneficiary>(ApiEndpoints.beneficiaries.base, beneficiary);
  }

  /**
   * Crea un nuevo beneficiario dentro de un partner.
   * POST /partners/{partnerId}/beneficiaries
   *
   * @param partnerId - ID del partner donde crear el beneficiario
   * @param beneficiary - Datos del beneficiario a crear
   * @returns Observable con el beneficiario creado
   */
  createInPartner(partnerId: string, beneficiary: BeneficiaryCreate): Observable<Beneficiary> {
    return this.http.post<Beneficiary>(
      ApiEndpoints.beneficiaries.createInPartner(partnerId),
      beneficiary,
    );
  }

  /**
   * Actualiza un beneficiario existente.
   * PUT /beneficiaries/{beneficiaryId} (UpdateBeneficiaryOperation)
   *
   * @param id - Identificador del beneficiario a actualizar
   * @param beneficiary - Datos actualizados del beneficiario
   * @returns Observable con el beneficiario actualizado
   */
  update(id: string, beneficiary: BeneficiaryUpdate): Observable<Beneficiary> {
    return this.http.put<Beneficiary>(ApiEndpoints.beneficiaries.byId(id), beneficiary);
  }

  /**
   * Elimina un beneficiario.
   * DELETE /beneficiaries/{beneficiaryId} (DeleteBeneficiaryOperation)
   *
   * @param id - Identificador del beneficiario a eliminar
   * @returns Observable vacío cuando el beneficiario se elimina correctamente
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.beneficiaries.byId(id));
  }

  /**
   * Obtiene beneficiarios paginados para exportación (método interno)
   */
  private getBeneficiariesPaginated(
    offset: number,
    limit: number,
    params: BeneficiaryListParams,
  ): Observable<PaginatedList<Beneficiary>> {
    let httpParams = new HttpParams();

    if (offset !== undefined) {
      httpParams = httpParams.set('offset', offset.toString());
    }
    if (limit !== undefined) {
      httpParams = httpParams.set('limit', limit.toString());
    }
    if (params?.partnerId) {
      httpParams = httpParams.set('partnerId', params.partnerId);
    }
    if (params?.filter) {
      httpParams = httpParams.set('filter', params.filter);
    }
    if (params?.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      httpParams = httpParams.set('sortOrder', params.sortOrder);
    }

    return this.http.get<PaginatedList<Beneficiary>>(ApiEndpoints.beneficiaries.base, {
      params: httpParams,
      context: skipGlobalLoading(),
    });
  }

  /**
   * Obtiene todos los beneficiarios para exportación (hace múltiples llamadas hasta obtener todos)
   */
  getAllForExport(params?: BeneficiaryListParams): Observable<Beneficiary[]> {
    return this.getBeneficiariesPaginated(0, 100, params || {}).pipe(
      concatMap((firstResponse) => {
        const total = firstResponse.total;

        if (total <= 100 || total === 0) {
          return of(firstResponse.items);
        }

        const totalPages = Math.ceil(total / 100);
        const requests: Observable<PaginatedList<Beneficiary>>[] = [of(firstResponse)];

        for (let page = 1; page < totalPages; page++) {
          const offset = page * 100;
          requests.push(this.getBeneficiariesPaginated(offset, 100, params || {}));
        }

        return forkJoin(requests).pipe(
          map((responses) => {
            const allBeneficiaries: Beneficiary[] = [];
            responses.forEach((response) => {
              allBeneficiaries.push(...response.items);
            });
            return allBeneficiaries;
          }),
        );
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // GDPR - Gestión de documentos GDPR
  // ---------------------------------------------------------------------------

  /**
   * Descarga el documento GDPR de un beneficiario.
   * GET /beneficiaries/{beneficiaryId}/gdpr (GetBeneficiaryGdprOperation)
   *
   * @param beneficiaryId - ID del beneficiario
   * @returns Observable con el archivo como Blob
   */
  downloadGdpr(beneficiaryId: string): Observable<Blob> {
    return this.http.get(ApiEndpoints.beneficiaries.gdpr(beneficiaryId), {
      responseType: 'blob',
    });
  }

  /**
   * Sube o actualiza el documento GDPR de un beneficiario.
   * PUT /beneficiaries/{beneficiaryId}/gdpr (CreateBeneficiaryGdprOperation)
   *
   * @param beneficiaryId - ID del beneficiario
   * @param file - Archivo a subir
   * @returns Observable vacío cuando se completa
   */
  uploadGdpr(beneficiaryId: string, file: File): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/octet-stream',
      'X-File-Name': file.name,
      'X-File-Type': file.type || 'application/octet-stream',
    });

    return this.http.put<void>(ApiEndpoints.beneficiaries.gdpr(beneficiaryId), file, {
      headers,
    });
  }

  /**
   * Elimina el documento GDPR de un beneficiario.
   * DELETE /beneficiaries/{beneficiaryId}/gdpr (DeleteBeneficiaryGdprOperation)
   *
   * @param beneficiaryId - ID del beneficiario
   * @returns Observable vacío cuando se completa
   */
  deleteGdpr(beneficiaryId: string): Observable<void> {
    return this.http.delete<void>(ApiEndpoints.beneficiaries.gdpr(beneficiaryId));
  }
}
